import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Hooks
import { useReferenceState } from "@/Common/hooks/ReferenceState";

// Helpers
import { isEmptyLastTierCommission, isProductDefined } from "./utils";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import CommissionModel from "./CommissionModel";

// MUI
import { Grid } from "@mui/material";

const modelMap = (data) => ({
	new: data?.["commissions"]?.["new"] || null,
	preOwned: data?.["commissions"]?.["preOwned"] || null,
	globalGrossAndUnits: data?.["tabsProps"]?.["globalGrossAndUnits"] || false,
	isGuaranteedDraw: Boolean(data?.["tabsProps"]?.["isGuaranteedDraw"]),
});

const isSomeConfiguredModel = (model) => {
	const { commissions } = model || {};
	if (!commissions) {
		return true;
	}
	return Object.entries(commissions)?.some(([_, value]) => !isEmptyLastTierCommission(value) || isProductDefined(value));
};

export default function DefaultCommissions(props) {
	const {
		sectionId = "default",
		commissions = {
			new: null,
			preOwned: null,
		},
		backProducts = [],
		tabsProps = {
			tabs: ["New Vehicles", "Pre-Owned Vehicles"],
			errors: [],
			globalGrossAndUnits: false,
			isGuaranteedDraw: false,
		},
		loading = false,
		loadingProducts = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	const maxProductPenetration = backProducts?.reduce((acc, product) => acc + (product?.contractUnits || 0), 0) || 0;
	const backendProductsCount = maxProductPenetration || 10000;

	// Hooks
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Control to send update actions / References
	const [, setIsValidAreas, validAreas, recheckValidAreas] = useReferenceState(true, []);

	// States
	const wasUpdated = useRef(false);
	const [model, setModel] = useState(null);
	const isSomeModelConfigured = isSomeConfiguredModel(model);

	// Error handler
	const errors = useMemo(() => {
		const errorNew = tabsProps.errors?.includes(getErrorKey(sectionId, "new"));
		const errorPreOwned = tabsProps.errors?.includes(getErrorKey(sectionId, "preOwned"));
		return [errorNew, errorPreOwned];
	}, [sectionId, tabsProps.errors, getErrorKey]);

	// Methods
	const update = (data) => {
		wasUpdated.current = true;
		setModel(data);
		const isSomeDefined = isSomeConfiguredModel(data);
		onActionUpdate?.(sectionId, null, modelMap(data), isSomeDefined);
	};

	const handleUpdateSwitches = (area, value) => {
		const updatedTabsProps = { ...model?.tabsProps, [area]: value };
		const updatedModel = { ...model, tabsProps: updatedTabsProps };
		update(updatedModel);
	};

	const handleUpdateCommissions = (area, data) => {
		const updatedCommissions = { ...model?.commissions, [area]: data };
		const updatedModel = { ...model, commissions: updatedCommissions };
		const index = area === "preOwned" ? 1 : 0;
		validAreas[index] = true;
		recheckValidAreas();
		update(updatedModel);
	};

	const handleValidation = (area, value) => {
		const index = area === "preOwned" ? 1 : 0;
		validAreas[index] = value;
		recheckValidAreas();
		value ? removeSectionError(sectionId, area) : addSectionError(sectionId, area);
	};

	useEffect(() => {
		setModel({
			tabsProps,
			commissions,
		});

		return () => {
			setModel(null);
		};
	}, [tabsProps, commissions, setModel]);

	useEffect(() => {
		return () => {
			setIsValidAreas(true);
			clearSectionErrors(sectionId);
		};
	}, [sectionId, setIsValidAreas, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid mt={2}>
						<TabLoader switch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					!!model?.commissions && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={errors}
							content={Object.entries(model?.commissions)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<CommissionModel
											model={value}
											backProducts={backProducts || []}
											backendProductsCount={backendProductsCount}
											isSomeConfigured={isSomeModelConfigured}
											loadingProducts={loadingProducts}
											readOnly={readOnly}
											onActionUpdate={(updatedModel) => handleUpdateCommissions(key, updatedModel)}
											onActionValidation={(updatedValidation) => handleValidation(key, updatedValidation)}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"isGuaranteedDraw"}
									textLeft={true}
									textChecked={"Guaranteed Draw"}
									textUnChecked={"Guaranteed Draw"}
									checked={Boolean(model?.tabsProps.isGuaranteedDraw)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches("isGuaranteedDraw", value)}
									tooltip={{
										title: "Guarantee the draw.",
										placement: "bottom",
										key: "default-commissions-guarantee-draw-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"globalGrossAndUnits"}
									textLeft={true}
									textChecked={"Global gross and units counts"}
									textUnChecked={"Global gross and units counts"}
									checked={Boolean(model?.tabsProps.globalGrossAndUnits)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches("globalGrossAndUnits", value)}
									tooltip={{
										title: "Uses all gross and units of New and Pre-Owned Vehicles together.",
										placement: "bottom",
										key: "default-commissions-global-gross-and-unit-counts-tooltip",
									}}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
DefaultCommissions.propTypes = {
	sectionId: PropTypes.string,
	commissions: PropTypes.object,
	backProducts: PropTypes.array,
	tabsProps: PropTypes.shape({
		tabs: PropTypes.array,
		errors: PropTypes.array,
		globalGrossAndUnits: PropTypes.bool,
		isGuaranteedDraw: PropTypes.bool,
	}),
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
